import {useMemo} from 'react';
import {type SortingState} from '@tanstack/react-table';
import {SortOrder} from '@netx/core-next/schema';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';

type DefaultSort = {
	field: string;
	order: 'asc' | 'desc';
};

export type UseSingleFieldSortingStateParams<
	S extends DefaultSort = DefaultSort,
> = {
	sort?: S;
	getDefaultSort: (sort?: Partial<S>) => S;
	onSortChange?: (sort: S) => void;
};

export const useSingleFieldSortingState = <
	S extends DefaultSort = DefaultSort,
>({
	sort,
	getDefaultSort,
	onSortChange,
}: UseSingleFieldSortingStateParams<S>) => {
	// we support a single column sort
	const setSortingState = useConstCallback(
		(state: SortingState | ((state: SortingState) => SortingState)) => {
			const result = typeof state === 'function' ? state(sortingState) : state;
			const defaultSort = getDefaultSort();
			// when we get in a sort state of reverting to default, the table lib actually
			// doesn't give us a field or order, so we need to handle that case
			let field = result[0] ? (result[0].id as S['field']) : undefined;
			let order = result[0] ? (result[0].desc ? 'desc' : 'asc') : undefined;
			if (field == null && field == null) {
				field = defaultSort.field;
				order =
					sort?.field !== defaultSort.field
						? defaultSort.order
						: sort?.order === SortOrder.DESCENDING
						? SortOrder.ASCENDING
						: SortOrder.DESCENDING;
			}
			onSortChange?.(
				getDefaultSort({
					field,
					order,
				} as S),
			);
		},
	);

	// but the table lib supports multiple
	const sortingState = useMemo(
		() => (!sort ? [] : [{id: sort?.field, desc: sort?.order === 'desc'}]),
		[sort],
	);

	return {
		setSortingState,
		sortingState,
	};
};
